"use client";

import { c as _c } from "react/compiler-runtime";
import ConditionalPromotionOffers from "../catalog/conditional-promotion-offers";
import React from "react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { ItemType } from "@/types/enums/item-type";
import type { GiftCardShopCartLine, ShopCartLine, ShopItem } from "@/types/shop";
import GiftCardConfigurator from "./configurator";
export type GiftCardConfiguratorSelection = {
  kind: "item";
  item: ShopItem;
} | {
  kind: "custom";
  item: ShopItem;
} | {
  kind: "edit";
  line: GiftCardShopCartLine;
};
interface GiftCardConfiguratorDialogProps {
  selection: GiftCardConfiguratorSelection | null;
  catalogItems?: ShopItem[];
  cartLines?: ShopCartLine[];
  onConfirm: (line: GiftCardShopCartLine, replacesLineId?: string) => void;
  onQuickAddPromotionItem?: (itemId: string, quantity: number) => void;
  onCancel: () => void;
}
const titleFor = (selection: GiftCardConfiguratorSelection | null): string => {
  if (!selection) return "Digital gift card";
  if (selection.kind === "item" || selection.kind === "custom") {
    return selection.item.name;
  }
  return selection.line.snapshot.name;
};

/**
 * Rebuild a `ShopItem`-shaped object from a preset cart line so editing reuses
 * the exact same configurator flow as adding. Without this, an edit on a
 * preset line lands in the configurator with `item=null`, which the
 * configurator treats as "custom amount" and silently converts the line.
 */
const itemFromPresetLine = (line: GiftCardShopCartLine): ShopItem | null => {
  if (line.kind !== "preset") return null;
  return {
    id: line.itemId,
    name: line.snapshot.name,
    description: line.snapshot.description,
    price: line.unitPriceCents,
    type: ItemType.GIFT_CARD,
    category: null,
    imageUrl: line.snapshot.imageUrl ?? null
  };
};
const GiftCardConfiguratorDialog: React.FC<GiftCardConfiguratorDialogProps> = t0 => {
  const $ = _c(43);
  const {
    selection,
    catalogItems: t1,
    cartLines: t2,
    onConfirm,
    onQuickAddPromotionItem,
    onCancel
  } = t0;
  let t3;
  if ($[0] !== t1) {
    t3 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const catalogItems = t3;
  let t4;
  if ($[2] !== t2) {
    t4 = t2 === undefined ? [] : t2;
    $[2] = t2;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const cartLines = t4;
  const open = selection !== null;
  const initialLine = selection?.kind === "edit" ? selection.line : undefined;
  let t5;
  if ($[4] !== initialLine || $[5] !== selection) {
    t5 = selection?.kind === "item" ? selection.item : initialLine ? itemFromPresetLine(initialLine) : null;
    $[4] = initialLine;
    $[5] = selection;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const item = t5;
  let t6;
  if ($[7] !== catalogItems || $[8] !== initialLine || $[9] !== item || $[10] !== selection) {
    t6 = selection?.kind === "custom" ? selection.item : initialLine?.kind === "preset" ? catalogItems.find(candidate => candidate.id === initialLine.itemId) : item;
    $[7] = catalogItems;
    $[8] = initialLine;
    $[9] = item;
    $[10] = selection;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const imageItem = t6;
  const imageUrl = imageItem ? imageItem.imageUrl : initialLine?.snapshot.imageUrl;
  let t7;
  if ($[12] !== imageUrl || $[13] !== initialLine?.lineId || $[14] !== onConfirm) {
    t7 = line => {
      onConfirm({
        ...line,
        snapshot: {
          ...line.snapshot,
          imageUrl: imageUrl ?? null
        }
      }, initialLine?.lineId);
    };
    $[12] = imageUrl;
    $[13] = initialLine?.lineId;
    $[14] = onConfirm;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const handleConfirm = t7;
  let t8;
  if ($[16] !== onCancel) {
    t8 = o => !o ? onCancel() : null;
    $[16] = onCancel;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== selection) {
    t9 = titleFor(selection);
    $[18] = selection;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t9) {
    t10 = <DialogTitle className="sr-only">{t9}</DialogTitle>;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <DialogDescription className="sr-only">Configure this digital gift card before adding it to your cart.</DialogDescription>;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== cartLines || $[24] !== catalogItems || $[25] !== handleConfirm || $[26] !== imageUrl || $[27] !== initialLine || $[28] !== item || $[29] !== onCancel || $[30] !== onQuickAddPromotionItem || $[31] !== open || $[32] !== selection) {
    t12 = open && <>{imageUrl && <img src={imageUrl} alt={titleFor(selection)} loading="lazy" className="mb-6 max-h-72 w-full rounded-xl bg-muted object-contain" />}{item && onQuickAddPromotionItem && <ConditionalPromotionOffers item={item} catalogItems={catalogItems} cartLines={cartLines} onQuickAdd={onQuickAddPromotionItem} />}<GiftCardConfigurator item={item} initialLine={initialLine} onConfirm={handleConfirm} onCancel={onCancel} /></>;
    $[23] = cartLines;
    $[24] = catalogItems;
    $[25] = handleConfirm;
    $[26] = imageUrl;
    $[27] = initialLine;
    $[28] = item;
    $[29] = onCancel;
    $[30] = onQuickAddPromotionItem;
    $[31] = open;
    $[32] = selection;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== t12) {
    t13 = <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6 sm:px-8 sm:py-8">{t12}</div>;
    $[34] = t12;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== t10 || $[37] !== t13) {
    t14 = <DialogContent className="max-w-lg gap-0 p-0">{t10}{t11}{t13}</DialogContent>;
    $[36] = t10;
    $[37] = t13;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== open || $[40] !== t14 || $[41] !== t8) {
    t15 = <Dialog open={open} onOpenChange={t8}>{t14}</Dialog>;
    $[39] = open;
    $[40] = t14;
    $[41] = t8;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  return t15;
};
export default GiftCardConfiguratorDialog;